import { forwardRef } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import DotsSpinner from '@components/ui/dots-spinner'
import { cn } from '@lib/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium antialiased focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:size-4 [&>svg]:fill-current',
  {
    variants: {
      variant: {
        primary: 'border border-transparent bg-o-inverse text-o-on-inverse hover:opacity-90',
        secondary: 'border border-o-line-strong bg-o-card text-o-ink hover:border-o-muted',
        ghost: 'bg-transparent text-o-slate hover:bg-o-chip hover:text-o-ink',
        danger: 'border border-o-danger-line bg-o-card text-o-danger hover:bg-o-danger-bg'
      },
      size: {
        xs: 'h-8 px-3 text-[13px] rounded-full',
        sm: 'h-9 px-4 text-sm rounded-full',
        md: 'h-10 px-4 text-sm rounded-full',
        lg: 'h-12 px-6 text-sm rounded-full'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'sm'
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {loading ? <DotsSpinner /> : children}
      </button>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
